// ---------------------------------------------------------------------------------------------
// ITEM 2 — **THE `/talk` CAPTURE, AND SHE IS READ BEFORE SHE SAYS A WORD.**
//
// Her order: *"the /talk capture page (her exact words: ASK LALA in gold, 'Any spiritual question.
// Real answers. Real guidance. Real predictions.', the invitation line, the safety disclaimer), the
// form (first name, email, birth date, time with Unknown, real place picker), the review carousel,
// the animated glowing generating state, then LALA ARRIVES..."*
//
// >>> IT IMPORTS NOTHING, WHICH IS THE ENTRY REQUIREMENT THAT LETS A `.mjs` GATE LOAD AND RUN EVERY
// LAW HERE (R146: reach is transitive) <<< rather than reading a regex out of a TSX file. The page
// composes no verdict of its own; it asks this and renders the answer.
//
// ════════════════════════════════════════════════════════════════════════════════════════════════
// >>> WHAT THIS FORM PRODUCES IS AN ACCOUNT, AND THAT IS A MEASURED DECISION RATHER THAN A DEFAULT.
// <<<
//
// FUNNEL-4's five free turns are fenced PER ACCOUNT, in the statements: `grantFreeTurns` carries
// `lala_free_turns_granted_at IS NULL` AND a `NOT EXISTS` over the account's whole Lala history, so
// *one allotment per account EVER* is arithmetic rather than a guard somebody remembered. **A lead
// row has no such fence.** Building the allotment on leads instead would be a second answer to *how
// many free turns has this person had* (R28/R29) with nothing underneath it — which is the
// free-reading firehose her own FUNNEL-4 clause 6 forbids by name.
//
// So this capture signs her up. Her ITEM 2 does not contradict that: it names the form and then
// says LALA ARRIVES, and an account created silently out of those five fields IS FUNNEL-4's
// signup-first with the friction taken out.
// ════════════════════════════════════════════════════════════════════════════════════════════════

/** The route this page really is, so nothing spells it twice (F4-1's own declaration is reused). */
export const TALK_CAPTURE_MARK = "talk-capture";

/**
 * >>> HER COPY. THE TWO SHE QUOTED ARE VERBATIM AND ARE HELD BYTE FOR BYTE. <<<
 *
 * *"ASK LALA in gold, 'Any spiritual question. Real answers. Real guidance. Real predictions.'"* —
 * those two are hers and a fixture asserts them character for character, because a paraphrase of a
 * line she dictated is the mockup-matching law broken in words rather than in pixels.
 *
 * **THE INVITATION AND THE SAFETY LINE ARE NAMED BY HER AND NOT QUOTED**, so they are written here
 * rather than guessed at elsewhere — and they are written to two rules. The invitation promises only
 * what this house really does (her chart IS cast before the first turn, which is `composeOpening`'s
 * whole point), because a promise the page cannot keep is R38/R165's trap in copy. The safety line
 * is the honest one a stranger off an advertisement is owed: this is guidance, it is not medical,
 * legal or financial advice, and it names where to go instead — a disclaimer that only protects the
 * house is not a safety line, it is a legal wall, and her own ruling on the funnel's trust lines is
 * that they are *a hand extended rather than a legal wall*.
 *
 * **RAW ENGLISH, BY F4-1's OWN PRECEDENT AND SAID OUT LOUD (R17/R160).** `TALK_COPY` is raw English
 * in `src/talkDoor.js` and ITEM 14b's census is green over it, so this door is in the
 * English-by-design class the funnel and the Angels drawer are already in (R60). It is one round to
 * translate the whole door the day she says so, and it is not taken here on my own word.
 */
export const TALK_CAPTURE_COPY = Object.freeze({
  // HER WORDS, VERBATIM.
  heading: "ASK LALA",
  sub: "Any spiritual question. Real answers. Real guidance. Real predictions.",

  // Her invitation line. It says what the details BUY her rather than asking for them (S1-A/R117:
  // name the act, never the machinery) — a stranger does not want to "complete a form", she wants
  // to be read.
  invitation: "Tell me who you are and I will read your chart before you ask your first question.",

  // Her safety line. Honest, warm, and it names the door rather than only the limit.
  safety: "Lala offers spiritual guidance for reflection. She is not a doctor, a lawyer or a financial adviser, and nothing here replaces one. If you are in danger or in crisis, please reach a professional or your local emergency service.",

  action: "Read me",
  working: "Reading your chart",

  // >>> **THE WAIT'S PERSISTENT STATUS LINE — HER COPY, VERBATIM, 2026-09-02.** <<<
  //
  // **MEASURED, WHICH IS WHY THIS SENTENCE EXISTS:** today's true median from submit to reading
  // delivered is **81 SECONDS**, and the week behind it runs 60-80. That is not a fault, it is what
  // a real cast costs — so for eighty seconds a stranger who has just handed over her birth details
  // is watching a ring breathe with nothing telling her anything is happening. The rotating
  // influence lines are ADVERTISING; they never once say *your chart is being pulled right now*.
  //
  // It sits ABOVE the rotating lines and it does NOT rotate, because the one thing she needs is the
  // thing that must never leave the screen. Present tense, personal, calm confidence — and it names
  // what will happen next ("a box will appear") so nobody thinks the page froze.
  waitStatus: "She's pulling your chart right now. Sit tight, a box will appear the moment she has you.",

  // T2-2s consent, in her own register rather than in the legal one. It is a CHECKBOX she ticks, so
  // the sentence names what she is agreeing to rather than pointing at a wall.
  consent: "I agree to the terms and the privacy policy.",

  // R83: the servers own words come first and this is the FLOOR beneath them, for the one case
  // where nobody said anything at all (a thrown request, an edge answering with no JSON in it).
  failed: "That did not go through just now. Try once more.",
});

/**
 * >>> HER FIVE FIELDS, AND THE SIXTH THE LAW DEMANDS. <<<
 *
 * Her order names first name, email, birth date, time with Unknown, and a real place picker. **T2-2
 * is why there is a consent tickbox beside them and it is not a field I chose to add**: NO ROOM IS
 * BUILT WITHOUT RECORDED CONSENT, enforced at `handleRoomCreate` above `ensureUser`, so a form that
 * shipped without it would submit perfectly and the room would refuse — her five fields filled in
 * and nothing to show for them, which is the worst version of this page.
 *
 * **EVERY OTHER FIELD THIS HOUSE ASKS FOR IS DELIBERATELY ABSENT AND THAT IS THE ROUND'S ONE
 * CONVERSION RULING.** `CreateRoomForm` is THE FRONT DOOR and asks for a middle name (numerology is
 * cast on the full name), a current location (N19-1) and a surname. On a page a paid click lands on,
 * **every required field is a lead lost** (F2's own measured ruling), so this asks the five she named
 * and no more. **The cost is NAMED rather than hidden (R17/R160):** an account born here can be read
 * astrologically and CANNOT be read numerologically until she fills the rest in, and her room's own
 * details editor is where that happens.
 */
export const TALK_FIELDS = Object.freeze(["firstName", "email", "birthDate", "birthTime", "birthplace", "consent"]);

/** Her time field is the one that may be answered with *I do not know*, and it is her own clause. */
export const TALK_UNKNOWN_FIELD = "birthTimeUnknown";

const str = (v) => (typeof v === "string" ? v.trim() : "");

/**
 * >>> WHAT THIS FORM IS MISSING, ONE ANSWER, ASKED BY THE PAGE AND BY THE DOOR ALIKE (R28/R29). <<<
 *
 * A page that decided this itself and a door that decided it again is two answers to *may she
 * submit*, and the copy that drifts is the one that lets a request through the browser and refuses
 * it at the server — which on this page is a paid click spent on a sentence she cannot act on.
 *
 * **THE TIME IS REQUIRED *UNLESS* SHE SAID SHE DOES NOT KNOW, WHICH IS HER OWN "with Unknown"
 * CLAUSE.** Collapsed either way it costs something real: required always, a stranger who genuinely
 * does not know her birth time cannot get in at all; never required, she leaves it blank without
 * being told what that costs her, and her houses cannot be cast (R18 — two facts, and only the
 * explicit one is a choice she made).
 *
 * **AND THE PLACE MUST BE RESOLVED, NOT MERELY TYPED.** PLACE-1 measured 55 of 61 members whose
 * chart could not be cast because a birthplace was typed and never geocoded, and every one of them
 * was told she had given us nothing. On the one page where a real picker is right there, an
 * unresolved place is refused BEFORE she waits on a reading that cannot happen.
 */
export function talkCaptureVerdict(form = {}) {
  const missing = [];
  const f = Object(form);

  if (!str(f.firstName)) missing.push("firstName");
  if (!str(f.email)) missing.push("email");
  if (!str(f.birthDate)) missing.push("birthDate");
  // Her clause: the time is required unless she ticked Unknown.
  if (f[TALK_UNKNOWN_FIELD] !== true && !str(f.birthTime)) missing.push("birthTime");
  if (!str(f.birthplace)) missing.push("birthplace");
  // A place she typed and never picked has no coordinates, so no chart can be cast from it.
  else if (!str(f.latitude) || !str(f.longitude)) missing.push("birthplace-unresolved");
  // T2-2, and it is the SERVER's law rather than this page's opinion.
  if (f.consent !== true) missing.push("consent");

  return Object.freeze({ ready: missing.length === 0, missing: Object.freeze(missing) });
}

/**
 * >>> HER ANIMATED GLOWING GENERATING STATE, AND WHAT IT MAY SAY. <<<
 *
 * S1-A/R117: a status line names what is happening to HER, never a colour, never a style, never the
 * machinery. So no stage here says *casting*, *API*, *payload*, *model* or *generating* — a stranger
 * waiting on her first reading is the last person who should be shown the plumbing.
 *
 * **BOTH ENDS OF THE PACE ARE DECLARED**, because *slow and luxurious* is satisfied by a line that
 * never changes (which is not a rotation) and *animated* is satisfied by a strobe. TRAP-1a's own
 * ruling on her ribbon, one surface over.
 */
// ================================================================================================
// >>> **HER 2026-08-27 RULING: THE WAIT IS THE WARM-UP ACT.** <<<
//
// Her words: *"while the chart casts (the ~83s hold), the rotating status lines are REPLACED: kill
// all machinery/AI-flavored talk, and rotate INFLUENCE lines in the house style (elegant, gold,
// confident, never carnival)."*
//
// **WHAT THIS REPLACES, AND WHY IT WAS NOT ENOUGH.** The four lines that stood here narrated the
// WAIT — *finding your place on the sky*, *she is almost with you*. They were clean of machinery and
// they were still the wrong job: eighty-three seconds is the longest attention a stranger will ever
// give this house before she has been given anything, and it was being spent describing a delay.
//
// **THE LINES ARE NOT IN THIS FILE. THEY ARE IN `server/data/talk-influence.json`.** Her clause:
// *"the lines live in config so she can add/edit without a build."* This module carries the
// DEFAULTS — which is what the page shows when the config cannot be read — and the LAW that decides
// which line is on screen. A default is not a duplicate: it is the answer to *the read failed and a
// stranger is still standing there*, and an empty wait screen on the page her advertising pays for
// is the one outcome this may not have.
//
// **ANY LINE IMPLYING A CLAIM MUST BE TRUE, AND THAT HALF IS HERS RATHER THAN A GATE'S.** The press
// features are real and are hers (Forbes, Essence, USA Today); *thousands of members* was measured
// against the live database on 2026-08-27 at **2,420 real member rows**. No gate can check the
// world. What a gate CAN check is the machinery ban, and it does.
// ================================================================================================
export const TALK_INFLUENCE = Object.freeze([
  "Lala has been featured in Forbes, Essence, and USA Today for her unmatched accuracy.",
  "Trusted by thousands of members.",
  "12th House is one of the biggest spiritual communities on the internet.",
  "Every reading is the raw truth. Nothing generic. Always Spot On.",
  "Members call her readings scary accurate.",
  "Nothing here is a horoscope. Every word is yours alone.",
  "Your chart has been waiting your whole life to be read properly.",
]);

// >>> **PACED CALMLY, WHICH IS A LONGER BEAT THAN THE STATUS LINES HAD (her clause).** <<< 2600ms was
// right for *she is almost with you* and is a flicker for a sentence naming three magazines. At
// 5200ms the seven defaults fill 36 seconds, so a ~83s wait sees the set roughly twice — read, not
// skimmed, and never a carousel spinning at somebody.
export const TALK_STAGE_MS = 5200;
export const TALK_STAGE_MIN_MS = 3200;
export const TALK_STAGE_MAX_MS = 9000;

/**
 * >>> **THE CONFIG'S LINES, OR THE DEFAULTS — AND A HALF-READ CONFIG IS NOT A SHORTER LIST.** <<<
 *
 * Every line is checked the way anything member-facing arriving off a file is checked: a real
 * non-empty string, trimmed, and nothing else. **A payload this house cannot read answers the
 * DEFAULTS rather than an empty array** (R19/R65): an empty list is a blank wait screen, and a blank
 * wait screen on her advertising's landing page is worse than a line that is one edit out of date.
 *
 * **IT IMPORTS NOTHING AND IS PURE**, so a `.mjs` gate RUNS this rather than reading it out of a
 * component (R146) — including the arm where the file is missing.
 */
export function talkInfluenceLines(raw) {
  const list = Array.isArray(raw) ? raw
    : Array.isArray(raw?.lines) ? raw.lines
      : null;
  if (!list) return TALK_INFLUENCE;
  const clean = [];
  for (const v of list) {
    if (typeof v !== "string") continue;
    const line = v.trim();
    // A duplicate is dropped rather than shown twice: two identical lines in a rotation read as the
    // screen having stopped.
    if (line && !clean.includes(line)) clean.push(line);
  }
  return clean.length ? Object.freeze(clean) : TALK_INFLUENCE;
}

/**
 * >>> THE THREE STATES THIS PAGE HAS, AND THE MIDDLE ONE IS THE ROUND (R18). <<<
 *
 * `form` is a stranger filling it in. `working` is her glowing generating state. `failed` is a real
 * refusal she can act on. **Collapsing `failed` into `form` puts her back on an untouched form with
 * no idea why nothing happened** — which reads as the page being broken, on the surface her
 * advertising pays for; collapsing it into `working` leaves her watching a glow forever.
 */
export function talkCaptureState({ busy = false, error = "" } = {}) {
  if (busy === true) return "working";
  if (str(error)) return "failed";
  return "form";
}

/**
 * Which line is on screen, given how long she has been waiting.
 *
 * >>> **IT LOOPS NOW, AND THE RULE IT REPLACES WAS RIGHT ABOUT THE THING IT WAS WRITTEN FOR
 * (MIGRATED AT ITS DECLARATION, DL-2/DT-2b/R181).** <<<
 *
 * The old law HELD on the last line, and its reason was exact: *a rotation that starts over tells
 * her the wait has restarted, and on a real cast the last line is the true one.* That is true of
 * STATUS lines — "she is almost with you" becoming "finding your place on the sky" again says the
 * work was thrown away.
 *
 * **HER INFLUENCE LINES ARE NOT STATUS, SO THAT REASONING DOES NOT REACH THEM.** None of them claims
 * anything about progress; they are what this house is. Holding on the last one for the back half of
 * an eighty-three second wait would freeze the screen on a single sentence, which is the *"never a
 * rotation"* failure TRAP-1a names one surface over. So it loops, and looping now says nothing false
 * because no line was ever a promise about time.
 *
 * The rotation is INDEX-ONLY arithmetic — no clock of its own, no state — so a gate RUNS every
 * position of it without a browser (R146).
 */
export function talkInfluenceAt(elapsedMs, lines = TALK_INFLUENCE, everyMs = TALK_STAGE_MS) {
  const list = Array.isArray(lines) ? lines.filter((s) => typeof s === "string" && s.trim()) : [];
  if (!list.length) return "";
  const step = typeof everyMs === "number" && Number.isFinite(everyMs) && everyMs > 0 ? everyMs : TALK_STAGE_MS;
  const ms = typeof elapsedMs === "number" && Number.isFinite(elapsedMs) && elapsedMs > 0 ? elapsedMs : 0;
  return list[Math.floor(ms / step) % list.length];
}
